import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactMapGL, { NavigationControl, WebMercatorViewport } from 'react-map-gl';
import pick from 'lodash/pick';
import ListingClusters from './ListingClusters';
import { listingSearchPerformed as listingSearch } from '../../actions';
import { usePrevious } from '../../helpers/utils';
import { preparePayload, trackDebounce } from '../../helpers/searchResultsAnalytics';

const mapboxToken = 'pk.eyJ1Ijoic3RmLWVuZyIsImEiOiJjazY4MXk5d3cwMGZ4M25xajk0eWdqcWZ1In0.9PKdrLg0yDExox06501TDg';
let firstSearch = true;

export default function () {
  const [mapRef, setMapRef] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const dispatch = useDispatch();

  const {
    latitude, longitude, zoom, minZoom, maxZoom, mapVisible,
  } = useSelector((state) => state.map);
  const { markers } = useSelector((state) => state.searchResults);
  const { parameters } = useSelector((state) => state.searchFilters);

  const previousParams = usePrevious(parameters);

  useEffect(() => {
    if (!mapVisible) {
      return;
    }

    const mapEl = window.document.getElementById('map-container');
    const { offsetWidth: mapWidth, offsetHeight: mapHeight } = mapEl;

    if (mapWidth !== 0) {
      const fitBounds = getFitBounds({ ...parameters, mapWidth, mapHeight });

      if (fitBounds) {
        window.document.getElementById('static-map').style.backgroundImage = 'url(\'https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/'
          + `${fitBounds.longitude},`
          + `${fitBounds.latitude},`
          + `${fitBounds.zoom}/`
          + `${mapWidth}x${mapHeight}?`
          + `access_token=${mapboxToken}')`;
      }
    }
  }, []);

  useEffect(() => {
    if (!firstSearch
      && parameters.longitude
      && parameters.latitude
      && parameters.zoom
      && (
        !previousParams
        || parameters.longitude !== previousParams.longitude
        || parameters.latitude !== previousParams.latitude
        || parameters.zoom !== previousParams.zoom
      )) {
      dispatch(listingSearch(window.searchCoreMarket || {}));

      dispatch({
        type: 'SET_MAP_VIEWPORT',
        payload: {
          longitude: parameters.longitude,
          latitude: parameters.latitude,
          zoom: parameters.zoom || 11,
          transitionDuration: 0,
        },
      });
    } else if (firstSearch
      && parameters.longitude
      && parameters.latitude
      && parameters.latitude_gteq
      && parameters.latitude_lteq
      && parameters.longitude_gteq
      && parameters.longitude_lteq
    ) {
      // First search - Fit bounds
      // TODO: Move this to initial loading / default state
      const fitBounds = getFitBounds({
        ...parameters,
        // Adapt to CSS responsive rules
        mapWidth: (window.innerWidth >= 975) ? window.innerWidth * 0.45 : document.body.clientWidth * 0.35,
        mapHeight: window.innerHeight - 130,
      });

      dispatch({
        type: 'SET_MAP_VIEWPORT',
        payload: {
          zoom: fitBounds.zoom,
          longitude: fitBounds.longitude,
          latitude: fitBounds.latitude,
        },
      });
    }

    firstSearch = false;
  }, [parameters]);

  function updateViewport(data, e) {
    if (e && (e.isPanning || e.isDragging)) {
      trackDebounce('Listings Search Map Dragged', preparePayload(parameters).merge(data).value());
    } else if (e && e.isZooming) {
      trackDebounce('Listings Search Map Zoomed', preparePayload(parameters).merge(data).value());
    } else if (e && e.isDragging === false) {
      // Skips viewport update on first search
      return;
    }

    dispatch({
      type: 'SET_MAP_VIEWPORT',
      payload: {
        ..._.pick(data, [
          'altitude', 'bearing', 'height', 'latitude', 'longitude',
          'maxPitch', 'maxZoom', 'minPitch', 'minZoom', 'pitch', 'width', 'zoom',
        ]),
      },
    });
  }

  return (
    <div id="search-map">
      <div id="map-container" className={mapVisible ? undefined : 'map-hidden'} data-scroll-anchor-to-footer>
        <ReactMapGL
          id="map"
          zoom={zoom}
          latitude={latitude}
          longitude={longitude}
          ref={(ref) => ref && setMapRef(ref.getMap())}
          width="100%"
          height="100%"
          minZoom={minZoom}
          maxZoom={maxZoom}
          mapStyle="mapbox://styles/mapbox/streets-v11?optimize=true"
          mapboxApiAccessToken={mapboxToken}
          onViewportChange={updateViewport}
          onLoad={useCallback(() => {
            setIsMapLoaded(true);
          })}
          onClick={(e) => {
            if (e.target.className !== 'marker') {
              dispatch({ type: 'SELECT_MAP_MARKER', payload: null });
            }
          }}
        >
          {
            mapRef && (
              <ListingClusters
                map={mapRef}
                markers={markers}
              />
            )
          }

          <div style={{ position: 'absolute', left: 8, bottom: 98 }}>
            <NavigationControl showCompass={false} />
          </div>
        </ReactMapGL>
        {
          /* Debug coordinates
            <div id="debug-info">
              Lng: {viewport.longitude} | Lat: {viewport.latitude} | Zoom: {viewport.zoom}
            </div>
          */
        }
      </div>
      {mapVisible && !isMapLoaded && (<div id="static-map" />)}
    </div>
  );
}

function getFitBounds({
  longitude, latitude, longitude_gteq, longitude_lteq, latitude_gteq, latitude_lteq, zoom, mapWidth, mapHeight,
}) {
  if (!mapHeight || !mapWidth || !latitude_lteq || !latitude_gteq || !longitude_lteq || !longitude_gteq || !latitude || !longitude) {
    return;
  }

  const vp = new WebMercatorViewport({
    longitude,
    latitude,
    zoom: zoom || 11,
    bearing: 0,
    pitch: 0,
    width: mapWidth,
    height: mapHeight,
  });

  return vp.fitBounds([
    [longitude_gteq, latitude_gteq],
    [longitude_lteq, latitude_lteq],
  ], { padding: 10 });
}
